import {RouteProp, useRoute} from '@react-navigation/native';
import React, {useMemo} from 'react';
import {FlatList, TouchableOpacity} from 'react-native';

import PlayerBar from '@/components/PlayerBar/PlayerBar';
import ProfileInfo from '@/components/ProfileInfo';
import Screen from '@/components/Screen/Screen';
import ScreenError from '@/components/ScreenError';
import ScreenLoader from '@/components/ScreenLoader';
import Space from '@/components/Space/Space';
import Text from '@/components/Text/Text';
import TrackBlockEmbed from '@/components/TrackBlock/TrackBlockEmbed';
import TrackCardEmbed from '@/components/TrackCard/TrackCardEmbed';
import {useEmbedColorsListener} from '@/hooks/useEmbedColorsListener';
import {useEmbedPlayer} from '@/hooks/useEmbedPlayer';
import {useEmbedCollectionQuery} from '@/queries/collection';
import {useCustomColors, useThemedStyles, useOverrideTheme} from '@/theme';
import {EmbedStackParams, Routes} from '@/types/routes';
import {getCollectorUrl} from '@/utils/share';
import {getUserSlug} from '@/utils/user';

import {styles} from './Collection.style';

const MINIMAL_BANNER_HEIGHT = 450;

const Separator = () => <Space mt="xs" />;

const EmbedCollection = () => {
  const style = useThemedStyles(styles);
  const {
    params: {slug, colors, theme, platform, grid},
  } = useRoute<RouteProp<EmbedStackParams, Routes.EmbedCollection>>();
  const {
    user,
    collection,
    query: {isLoading, isError},
  } = useEmbedCollectionQuery(slug);

  const filteredCollection = useMemo(() => {
    return platform
      ? collection.filter(track => track.platformId === platform)
      : collection;
  }, [platform, collection]);

  const {
    currentTrack,
    isPlaying,
    togglePlay,
    playTrack,
    hasNext,
    next,
    previous,
  } = useEmbedPlayer(filteredCollection);

  useOverrideTheme({predefinedThemeName: theme});
  useCustomColors(colors);
  useEmbedColorsListener();

  const onPlayOnSpinampPress = () => {
    if (user) {
      window.open(getCollectorUrl(getUserSlug(user)));
    }
  };

  if (isLoading) {
    return <ScreenLoader />;
  }

  if (!user || isError) {
    return <ScreenError textId="notFound" />;
  }

  const minimalBanner = window.innerHeight < MINIMAL_BANNER_HEIGHT;

  const ListHeaderComponent = (
    <>
      <Space style={style.header}>
        <Space mt="xxs" />
        <ProfileInfo user={user} />
        <Space mt={'xl'} />
        <TouchableOpacity onPress={onPlayOnSpinampPress}>
          <Text size={'xs'} id="embed.listen" />
        </TouchableOpacity>
      </Space>
      <Space mt="xs" />
    </>
  );

  const RenderComponent = grid ? TrackBlockEmbed : TrackCardEmbed;

  return (
    <Screen>
      <FlatList
        numColumns={grid ? 100000 : 1}
        contentContainerStyle={style.contentContainerStyle}
        ItemSeparatorComponent={Separator}
        showsVerticalScrollIndicator={false}
        columnWrapperStyle={
          grid
            ? {
                alignContent: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }
            : undefined
        }
        data={filteredCollection}
        keyExtractor={track => track.id}
        renderItem={({item: track}) => (
          <RenderComponent
            track={track}
            isCurrentTrack={track.id === currentTrack?.id}
            isPlaying={isPlaying}
            onPlay={() => playTrack(track)}
            onTogglePlay={togglePlay}
            count={track.count}
            showArtist
          />
        )}
        ListHeaderComponent={minimalBanner ? undefined : ListHeaderComponent}
      />
      <PlayerBar
        currentTrack={currentTrack}
        isPlaying={isPlaying}
        onPlayPress={togglePlay}
        onNextPress={next}
        onPreviousPress={previous}
        hasNext={hasNext}
        fullPlayerEnabled={false}
        hideImage={minimalBanner}
      />
    </Screen>
  );
};

export default EmbedCollection;
